<template>
  <div class="bg-white border border-gray-200 rounded-lg mt-4">
    <BoostRewardsControls
      @update:date-range="onDateRangeChange"
      @update:clear-filters="onClearFilters"
      @update:search-term="onSearchTermChange"
    />

    <DataTable
      :is-loading="isLoading"
      :columns="boostRewardsTableColumns"
      :data="paginatedBoostRewards"
      :total-pages="totalPages"
      :total-count="totalCount"
      :page-number="pageNumber"
      :page-size="pageSize"
      @update:page="onUpdatePage"
    />
  </div>
</template>

<script setup lang="ts">
import DataTable from '@/components/merchant/DataTable.vue';
import { NetworkError } from '@/errors';
import type { OrderServiceGetManyRequest } from '@/models/orders';
import { fetchOrders } from '@/services/orders';
import { displayDate } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { deepEqual } from '@/utils/paging.utils';
import type { DateRange } from 'radix-vue';
import { computed, onMounted, ref } from 'vue';
import type { ModelOrderResponse, OrderServiceApiOrderServiceGetManyRequest } from '@layup/api-sdk';
import BoostRewardsControls from '../boost-rewards-controls/BoostRewardsControls.vue';
import { boostRewardsTableColumns } from './BoostRewardsTableColumns';

const isLoading = ref(true);
const orderParams = ref<OrderServiceGetManyRequest>({
  limit: 100,
  state: 'COMPLETED',
  populate: ['voucher'],
  type: 'REWARDS',
});
const previousParams = ref<OrderServiceApiOrderServiceGetManyRequest>({ ...orderParams.value });
const boostRewards = ref<any[]>([]);

const paginatedBoostRewards = computed(() => {
  return boostRewards.value.slice((pageNumber.value - 1) * pageSize.value, pageNumber.value * pageSize.value);
});

const totalPages = ref(1);
const totalCount = ref(1);
const pageNumber = ref(1);
const pageSize = ref(10);

onMounted(async () => onUpdateFilter(orderParams.value, true));

const onUpdateFilter = async (params: OrderServiceApiOrderServiceGetManyRequest, skipCheck: boolean = false) => {
  if (!skipCheck) {
    if (deepEqual(params, previousParams.value)) {
      return;
    }
    previousParams.value = { ...params };
  }

  boostRewards.value = [];
  isLoading.value = true;

  try {
    const data = await fetchOrders(params);

    if (Object.keys(data).length > 0) {
      data.orders!.forEach((order: ModelOrderResponse) => {
        const reducedOrder = {
          id: order._id?.toString(),
          orderReference: order.reference,
          completedAt: displayDate(order.updatedAt),
          voucherCode: order.voucher?.last4Digits,
          voucherTotal: order.voucher?.initialValue,
        };
        boostRewards.value.push(reducedOrder);
      });

      totalPages.value = Math.ceil(boostRewards.value.length / pageSize.value);
      totalCount.value = boostRewards.value.length;
      pageNumber.value = 1;
    }
    addMetric('boost-rewards-table', { status: 'success', channel: 'merchant' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('boost-rewards-table', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('boost-rewards-table', { status: 'error', channel: 'merchant' });
    }
  } finally {
    isLoading.value = false;
  }
};

const onDateRangeChange = (value: DateRange): void => {
  orderParams.value.from = `${value.start?.year}-${value.start?.month}-${value.start?.day}`;
  orderParams.value.to = `${value.end?.year}-${value.end?.month}-${value.end?.day}`;
  onUpdateFilter(orderParams.value);
};

const onClearFilters = (): void => {
  orderParams.value = {
    limit: 100,
    state: 'COMPLETED',
    type: 'REWARDS',
  };
  onUpdateFilter(orderParams.value);
};

const onSearchTermChange = (value: string): void => {
  orderParams.value.search = value;
  onUpdateFilter(orderParams.value);
};

const onUpdatePage = (pageNo: number) => {
  pageNumber.value = pageNo;
};
</script>
